import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Droplets, Mail, Phone, MapPin, Clock, Truck } from 'lucide-react';
import NewsletterSignup from '../NewsletterSignup';
import { apiRequest } from '../../lib/utils';
import { PaymentBadgesCompact } from '../PaymentBadges';

export function Footer() {
    const { t } = useTranslation();
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const data = await apiRequest('/site-settings');
            setSettings(data);
        } catch (error) {
            console.error('Failed to load site settings:', error);
        }
    };

    // Use settings if available, otherwise use translations/defaults
    const phone = settings?.phone || t('footer.phone');
    const email = settings?.email || 'oca.directionpaca@gmail.com';
    const address = settings?.address || '5 place de la République';
    const city = settings?.city || 'Flayosc';
    const postalCode = settings?.postal_code || '83780';
    const siret = settings?.siret || '928 454 719 00016';
    const tva = settings?.tva || 'FR51 928 454 719';
    const pickupMessage = settings?.pickup_message || t('footer.pickupPoint');
    const openingHours = settings?.opening_hours?.monday_saturday || '9h00 - 12h00 / 16h00 - 19h00';

    // Format phone for tel: link
    const phoneLink = phone.replace(/\s/g, '');

    return (
        <footer className="bg-card border-t border-border" data-testid="footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
                    {/* Brand */}
                    <div className="space-y-4">
                        <Link to="/" className="flex items-center gap-2">
                            <Droplets className="h-8 w-8 text-primary" />
                            <span className="font-heading font-bold text-xl">{settings?.company_name || t('footer.company')}</span>
                        </Link>
                        <p className="text-muted-foreground text-sm">
                            {t('footer.description')}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-primary font-medium">
                            <Truck className="h-4 w-4" />
                            {pickupMessage}
                        </div>
                    </div>

                    {/* Navigation */}
                    <div>
                        <h4 className="font-heading font-semibold mb-4">{t('footer.navigation')}</h4>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/analysis" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.waterAnalysis')}</Link></li>
                            <li><Link to="/analysis/photo" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.photoAnalysis')}</Link></li>
                            <li><Link to="/calculateur" className="text-muted-foreground hover:text-primary transition-colors">Calculateur de dosage</Link></li>
                            <li><Link to="/chat" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.aiAssistant')}</Link></li>
                            <li><Link to="/shop" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.shop')}</Link></li>
                            <li><Link to="/appointment" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.makeAppointment')}</Link></li>
                            <li><Link to="/zones" className="text-muted-foreground hover:text-primary transition-colors">Zones d'intervention</Link></li>
                            <li><Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">Blog & Conseils</Link></li>
                            <li><Link to="/avis" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-link-avis">Avis clients</Link></li>
                            <li><Link to="/pro" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.proArea')}</Link></li>
                        </ul>
                    </div>

                    {/* Horaires */}
                    <div>
                        <h4 className="font-heading font-semibold mb-4">{t('footer.openingHours')}</h4>
                        <div className="space-y-1.5 text-sm">
                            <div className="flex items-start gap-2 text-muted-foreground">
                                <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <div className="space-y-1">
                                    <div className="flex justify-between gap-4">
                                        <span>Lundi</span>
                                        <span className="text-foreground">9h - 12h / 16h - 19h</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span>Mardi</span>
                                        <span className="text-foreground">9h - 12h / 16h - 19h</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span>Mercredi</span>
                                        <span className="text-foreground">9h - 12h / 16h - 19h</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span>Jeudi</span>
                                        <span className="text-foreground">9h - 12h / 16h - 19h</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span>Vendredi</span>
                                        <span className="text-foreground">9h - 12h / 16h - 19h</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span>Samedi</span>
                                        <span className="text-foreground">9h - 12h / 16h - 19h</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span>Dimanche</span>
                                        <span className="text-red-500">Fermé</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="font-heading font-semibold mb-4">{t('footer.contact')}</h4>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <a href={`tel:${phoneLink}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                                    <Phone className="h-4 w-4" />
                                    {phone}
                                </a>
                            </li>
                            <li>
                                <a href={`mailto:${email}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                                    <Mail className="h-4 w-4" />
                                    {email}
                                </a>
                            </li>
                            <li>
                                <a href={`https://maps.google.com/?q=${encodeURIComponent(`${address} ${postalCode} ${city}`)}`} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-muted-foreground hover:text-primary transition-colors">
                                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                    <span>{address}, {postalCode} {city}</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Newsletter */}
                    <div>
                        <NewsletterSignup source="footer" />
                    </div>
                </div>

                <div className="border-t border-border mt-8 pt-8">
                    {/* Social Media Section */}
                    <div className="flex flex-col items-center gap-6 mb-8">
                        <p className="font-heading font-semibold text-lg">Suivez-nous sur les réseaux !</p>
                        
                        <div className="flex flex-wrap justify-center gap-4">
                            {/* Facebook */}
                            <a 
                                href="https://www.facebook.com/share/1DeRwGFuX6/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-600/10 to-blue-400/10 rounded-2xl hover:shadow-lg transition-shadow"
                            >
                                <svg className="h-8 w-8 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                                <div className="text-left">
                                    <p className="font-semibold text-sm">Facebook</p>
                                    <p className="text-xs text-blue-600">O.C.A Piscine</p>
                                </div>
                                <img 
                                    src="/facebook-qr.png" 
                                    alt="QR Code Facebook" 
                                    className="h-16 w-16 rounded-lg bg-white p-1"
                                />
                            </a>

                            {/* Instagram */}
                            <a 
                                href="https://www.instagram.com/ocashop83" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-500/10 rounded-2xl hover:shadow-lg transition-shadow"
                            >
                                <svg className="h-8 w-8 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                                <div className="text-left">
                                    <p className="font-semibold text-sm">Instagram</p>
                                    <p className="text-xs text-pink-500">@ocashop83</p>
                                </div>
                                <img 
                                    src="/instagram-qr.png" 
                                    alt="QR Code Instagram" 
                                    className="h-16 w-16 rounded-lg bg-white p-1"
                                />
                            </a>

                            {/* TikTok */}
                            <a 
                                href="https://www.tiktok.com/@ocacx8" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-4 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-cyan-500/10 rounded-2xl hover:shadow-lg transition-shadow"
                            >
                                <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                </svg>
                                <div className="text-left">
                                    <p className="font-semibold text-sm">TikTok</p>
                                    <p className="text-xs text-primary">@ocacx8</p>
                                </div>
                                <img 
                                    src="/tiktok-qr.png" 
                                    alt="QR Code TikTok" 
                                    className="h-16 w-16 rounded-lg bg-white p-1"
                                />
                            </a>
                        </div>

                        {/* Phone CTA */}
                        <a 
                            href="tel:0651961474" 
                            className="flex items-center gap-3 px-6 py-3 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-opacity shadow-lg"
                        >
                            <Phone className="h-5 w-5" />
                            <span className="font-semibold">Appelez-nous : 06 51 96 14 74</span>
                        </a>
                    </div>

                    {/* Legal links */}
                    <div className="text-center text-xs text-muted-foreground space-y-4">
                        {/* Payment Methods */}
                        <div className="py-3">
                            <p className="text-xs text-muted-foreground mb-2">Moyens de paiement acceptés</p>
                            <PaymentBadgesCompact />
                        </div>

                        <div className="flex flex-wrap justify-center gap-4 mb-2">
                            <Link to="/cgv" className="hover:text-primary transition-colors">Conditions Générales de Vente</Link>
                            <span className="text-muted-foreground/50">|</span>
                            <Link to="/mentions-legales" className="hover:text-primary transition-colors">Mentions Légales</Link>
                            <span className="text-muted-foreground/50">|</span>
                            <Link to="/avis" className="hover:text-primary transition-colors">Avis</Link>
                            <span className="text-muted-foreground/50">|</span>
                            <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
                        </div>
                        <p>&copy; {new Date().getFullYear()} {settings?.company_name || t('footer.company')}. {t('footer.rights')}.</p>
                        <p>{t('footer.siret')} : {siret} | {t('footer.tva')} : {tva}</p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
